import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, BarChart3, Target, AlertTriangle, RefreshCw, MessageSquare } from "lucide-react";
import { fh } from "@/utils/hours";

const PERIOD_OPTIONS = [
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 180 days" },
  { value: 365, label: "Last 12 months" },
];

export default function QualityDashboard({ user }) {
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [days, setDays] = useState(90);
  const [projectFilter, setProjectFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (projectFilter !== "all") params.set("project_id", projectFilter);
      const res = await api.get(`/planning/quality-dashboard?${params.toString()}`);
      setData(res.data);
    } catch {
      toast.error("Failed to load quality data");
    } finally {
      setLoading(false);
    }
  }, [days, projectFilter]);

  useEffect(() => {
    api.get("/projects").then(r => {
      const arr = Array.isArray(r.data) ? r.data : (r.data?.projects || []);
      setProjects(arr.filter(p => p.planning_locked).map(p => ({ id: p.id, label: `${p.project_number} — ${p.name}` })));
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const KpiCard = ({ icon: Icon, label, value, suffix, color, hint, testid }) => (
    <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-md3-on-surface-variant font-medium">{label}</span>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
        <div className="text-3xl font-bold tabular-nums">
          {value}
          {suffix && <span className="text-base text-md3-on-surface-variant ml-1">{suffix}</span>}
        </div>
        {hint && <div className="text-[10px] text-md3-on-surface-variant mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );

  const kpis = data?.kpis || {};

  const fpyColor = (pct) => pct >= 80 ? "text-green-600" : pct >= 60 ? "text-amber-600" : "text-red-600";
  const reworkColor = (pct) => pct <= 10 ? "text-green-600" : pct <= 25 ? "text-amber-600" : "text-red-600";

  return (
    <Layout user={user}>
      <div className="p-6 space-y-5">
        <div className="flex justify-between items-end flex-wrap gap-3">
          <div>
            <h1 className="text-md3-display-small font-semibold flex items-center gap-3">
              <BarChart3 className="h-7 w-7 text-md3-primary" />
              Quality Dashboard
            </h1>
            <p className="text-md3-on-surface-variant text-sm">Producer-checker workflow quality metrics — derived from existing planning data.</p>
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <label className="text-xs text-md3-on-surface-variant block mb-1">Period</label>
              <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
                <SelectTrigger className="w-44 bg-white" data-testid="quality-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map(p => <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-md3-on-surface-variant block mb-1">Project</label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-72 bg-white" data-testid="quality-project"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-md3-primary" /></div>
        ) : !data ? (
          <div className="text-center py-12 text-md3-on-surface-variant">No data</div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard icon={Target} label="First-Pass Yield" value={kpis.first_pass_yield_pct} suffix="%" color={fpyColor(kpis.first_pass_yield_pct)} hint="Tasks approved on cycle 1" testid="kpi-fpy" />
              <KpiCard icon={RefreshCw} label="Avg Cycles per Task" value={kpis.avg_cycles_per_task} color="text-blue-600" hint="1 = no rework needed" testid="kpi-cycles" />
              <KpiCard icon={AlertTriangle} label="Rework %" value={kpis.rework_pct} suffix="%" color={reworkColor(kpis.rework_pct)} hint="Of total actual hours" testid="kpi-rework" />
              <KpiCard icon={MessageSquare} label="Comment Density" value={kpis.comment_density} color="text-md3-primary" hint="Comments per completed task" testid="kpi-comments" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <Card className="shadow-md3-1 border-0 bg-md3-surface-container">
                <CardContent className="p-4">
                  <div className="text-md3-on-surface-variant text-xs">Tasks Approved</div>
                  <div className="text-xl font-semibold">{kpis.total_tasks_done}</div>
                </CardContent>
              </Card>
              <Card className="shadow-md3-1 border-0 bg-md3-surface-container">
                <CardContent className="p-4">
                  <div className="text-md3-on-surface-variant text-xs">Total Actual Hours</div>
                  <div className="text-xl font-semibold">{fh(kpis.total_actual_hours)}</div>
                </CardContent>
              </Card>
              <Card className="shadow-md3-1 border-0 bg-md3-surface-container">
                <CardContent className="p-4">
                  <div className="text-md3-on-surface-variant text-xs">Total Rework Hours</div>
                  <div className="text-xl font-semibold text-orange-600">{fh(kpis.total_rework_hours)}</div>
                </CardContent>
              </Card>
            </div>

            {/* Producers + Checkers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container">
                <CardHeader><CardTitle className="text-md3-title-large">Producer Performance</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {(data.producers || []).length === 0 ? (
                    <div className="text-center py-6 text-md3-on-surface-variant text-sm">No producer data</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-md3-surface-container-high text-xs">
                        <tr className="text-left">
                          <th className="px-3 py-2">User</th>
                          <th className="px-3 py-2 text-right">Tasks</th>
                          <th className="px-3 py-2 text-right">FPY %</th>
                          <th className="px-3 py-2 text-right">Actual Hrs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.producers.map(p => (
                          <tr key={p.user_id} className="border-t border-md3-outline-variant">
                            <td className="px-3 py-2">{p.user_name}</td>
                            <td className="px-3 py-2 text-right">{p.tasks_done}</td>
                            <td className="px-3 py-2 text-right">
                              <Badge className={fpyColor(p.fpy_pct).replace("text-", "text-").replace("-600", "-700") + " bg-opacity-10"}>
                                <span className={fpyColor(p.fpy_pct)}>{p.fpy_pct}%</span>
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{fh(p.actual_hours)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container">
                <CardHeader><CardTitle className="text-md3-title-large">Checker Activity</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {(data.checkers || []).length === 0 ? (
                    <div className="text-center py-6 text-md3-on-surface-variant text-sm">No checker data</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-md3-surface-container-high text-xs">
                        <tr className="text-left">
                          <th className="px-3 py-2">User</th>
                          <th className="px-3 py-2 text-right">Tasks Reviewed</th>
                          <th className="px-3 py-2 text-right">Send-Backs</th>
                          <th className="px-3 py-2 text-right">Comments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.checkers.map(c => (
                          <tr key={c.user_id} className="border-t border-md3-outline-variant">
                            <td className="px-3 py-2">{c.user_name}</td>
                            <td className="px-3 py-2 text-right">{c.tasks_reviewed}</td>
                            <td className="px-3 py-2 text-right">{c.send_backs}</td>
                            <td className="px-3 py-2 text-right">{c.comments_issued}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Per-project */}
            <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container">
              <CardHeader><CardTitle className="text-md3-title-large">Project Quality (sorted lowest FPY first)</CardTitle></CardHeader>
              <CardContent className="p-0">
                {(data.projects || []).length === 0 ? (
                  <div className="text-center py-6 text-md3-on-surface-variant text-sm">No completed tasks in period</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-md3-surface-container-high text-xs">
                      <tr className="text-left">
                        <th className="px-3 py-2">Project #</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2 text-right">Tasks Approved</th>
                        <th className="px-3 py-2 text-right">FPY %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.projects.map(p => (
                        <tr key={p.project_id} className="border-t border-md3-outline-variant">
                          <td className="px-3 py-2 font-mono text-xs">{p.project_number}</td>
                          <td className="px-3 py-2 truncate max-w-md">{p.project_name}</td>
                          <td className="px-3 py-2 text-right">{p.tasks_done}</td>
                          <td className={`px-3 py-2 text-right font-medium ${fpyColor(p.fpy_pct)}`}>{p.fpy_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
